/**
 * Teacher Registration Page
 * First teacher from a school becomes admin automatically
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '@/hooks/useNotification';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { apiRequest } from '@/api/client';

interface RegisterFormData {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
}

export const RegisterPage = () => {
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<RegisterFormData>({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof RegisterFormData, string>>>({});
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (field: keyof RegisterFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof RegisterFormData, string>> = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.firstName) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName) {
      newErrors.lastName = 'Last name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    setIsLoading(true);
    
    try {
      // Call teacher_register Lambda function
      const response = await apiRequest('teacher_register', {
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        gradeLevel: '',
      });
      
      if (response.success) {
        showNotification('success', 'Registration successful! Please check your email for a verification code.');
        
        // Redirect to verification page
        setTimeout(() => {
          navigate(`/verify-email?email=${encodeURIComponent(formData.email)}`);
        }, 1500);
      } else {
        // Handle specific error codes
        if (response.error?.includes('No school license found')) {
          showNotification('error', 'No school license found for your email domain. Please purchase a license first.');
          setTimeout(() => {
            window.location.href = 'https://spelltales.co.uk/school-pricing.html';
          }, 3000);
        } else if (response.error?.includes('license is inactive')) {
          showNotification('error', 'Your school license is inactive. Please contact your administrator.');
        } else if (response.error?.includes('license has expired')) {
          showNotification('error', 'Your school license has expired. Please renew your subscription.');
        } else if (response.error?.includes('registration is closed')) {
          showNotification('error', 'Teacher registration is closed. Please contact your school administrator to create an account for you.');
        } else if (response.error?.includes('UsernameExistsException') || response.error?.includes('already exists')) {
          showNotification('error', 'An account with this email already exists. Please sign in instead.');
          setTimeout(() => {
            navigate('/login');
          }, 2000);
        } else {
          showNotification('error', response.error || 'Registration failed');
        }
      }
    } catch (error) {
      console.error('Registration error:', error);
      showNotification('error', 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-indigo-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 mb-4">
            <img src="/admin/icon.png" alt="SpellTales" className="w-16 h-16 rounded-2xl" />
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 mb-2">
            Teacher Registration
          </h1>
          <p className="text-slate-600">Create your school admin account</p>
        </div>

        {/* Registration Form */}
        <div className="bg-white rounded-3xl shadow-xl border border-slate-200 p-8">
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
            <p className="text-sm text-emerald-800">
              <strong>First teacher?</strong> You'll automatically become the school administrator!
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="First Name"
                type="text"
                value={formData.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                error={errors.firstName}
                placeholder="John"
                required
                autoComplete="given-name"
              />

              <Input
                label="Last Name"
                type="text"
                value={formData.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
                error={errors.lastName}
                placeholder="Smith"
                required
                autoComplete="family-name"
              />
            </div>

            <Input
              label="School Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              error={errors.email}
              placeholder="teacher@school.edu"
              required
              autoComplete="email"
              helperText="Use your school email address"
            />

            <Input
              label="Password"
              type="password"
              value={formData.password}
              onChange={(e) => handleChange('password', e.target.value)}
              error={errors.password}
              placeholder="••••••••"
              required
              autoComplete="new-password"
              helperText="At least 8 characters"
            />

            <Input
              label="Confirm Password"
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => handleChange('confirmPassword', e.target.value)}
              error={errors.confirmPassword}
              placeholder="••••••••"
              required
              autoComplete="new-password"
            />

            <Button
              type="submit"
              variant="primary"
              size="lg"
              isLoading={isLoading}
              className="w-full mt-6"
            >
              Create Account
            </Button>
          </form>

          {/* Login Link */}
          <div className="mt-6 pt-6 border-t border-slate-200 text-center">
            <p className="text-sm text-slate-600">
              Already have an account?{' '}
              <button
                onClick={() => navigate('/login')}
                className="text-emerald-600 hover:text-emerald-700 font-semibold"
              >
                Sign In
              </button>
            </p>
          </div>

          {/* Purchase License Link */}
          <div className="mt-4 pt-4 border-t border-slate-200">
            <p className="text-center text-sm text-slate-600 mb-3">
              Don't have a school license?
            </p>
            <a
              href="https://spelltales.co.uk/school-pricing.html"
              className="block w-full text-center px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-900 font-semibold rounded-xl transition-colors"
            >
              Purchase a License
            </a>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-xs text-slate-500">
          <p>© 2026 SpellTales. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};
